import React, { Suspense, lazy } from 'react'
import type { BusinessType } from '../components/BusinessTypeSelector'

// Sales sections (existing)
import HeroSection from '../components/sections/HeroSection'
const FeaturesSection = lazy(() => import('../components/sections/FeaturesSection'))
const AttendanceDemo = lazy(() => import('../components/sections/AttendanceDemo'))
const DashboardPreview = lazy(() => import('../components/sections/DashboardPreview'))

// School sections
const SchoolHeroSection = lazy(() => import('../components/sections/SchoolHeroSection'))
const SchoolFeaturesSection = lazy(() => import('../components/sections/SchoolFeaturesSection'))
const BusTrackingDemo = lazy(() => import('../components/sections/BusTrackingDemo'))

// Transport sections
const TransportHeroSection = lazy(() => import('../components/sections/TransportHeroSection'))
const TransportFeaturesSection = lazy(() => import('../components/sections/TransportFeaturesSection'))

// Shared sections
const TestimonialsSection = lazy(() => import('../components/sections/TestimonialsSection'))
const IntegrationsSection = lazy(() => import('../components/sections/IntegrationsSection'))
const ContactSection = lazy(() => import('../components/sections/ContactSection'))
const Footer = lazy(() => import('../components/sections/Footer'))

import { LazySection } from '../components/ui/lazy-section'

interface LandingPageProps {
  businessType: BusinessType
  onChangeBusinessType: () => void
}

const LandingPage: React.FC<LandingPageProps> = ({ businessType, onChangeBusinessType }) => {
  React.useEffect(() => {
    const timer = setTimeout(() => {
      import('../components/sections/FeaturesSection')
      import('../components/sections/AttendanceDemo')
    }, 100)
    return () => clearTimeout(timer)
  }, [])

  const SectionSkeleton = () => (
    <div className="py-24 animate-pulse">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="h-8 bg-neutral-200 rounded-full w-3/4 mx-auto mb-4" />
          <div className="h-4 bg-neutral-200 rounded w-full mb-2" />
          <div className="h-4 bg-neutral-200 rounded w-2/3 mx-auto" />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className="h-64 bg-neutral-200 rounded-lg" />
          ))}
        </div>
      </div>
    </div>
  )

  // Sales landing (original)
  if (businessType === 'sales') {
    return (
      <div className="min-h-screen">
        <HeroSection onChangeBusinessType={onChangeBusinessType} />
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <FeaturesSection />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <AttendanceDemo />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <DashboardPreview />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <TestimonialsSection />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <IntegrationsSection />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <ContactSection />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="100px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <Footer onChangeBusinessType={onChangeBusinessType} />
          </Suspense>
        </LazySection>
      </div>
    )
  }

  // School landing
  if (businessType === 'school') {
    return (
      <div className="min-h-screen">
        <Suspense fallback={<SectionSkeleton />}>
          <SchoolHeroSection onChangeBusinessType={onChangeBusinessType} />
        </Suspense>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <SchoolFeaturesSection />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <BusTrackingDemo />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <AttendanceDemo />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <TestimonialsSection />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <ContactSection />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="100px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <Footer onChangeBusinessType={onChangeBusinessType} />
          </Suspense>
        </LazySection>
      </div>
    )
  }

  // Transport landing
  if (businessType === 'transport') {
    return (
      <div className="min-h-screen">
        <Suspense fallback={<SectionSkeleton />}>
          <TransportHeroSection onChangeBusinessType={onChangeBusinessType} />
        </Suspense>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <TransportFeaturesSection />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <BusTrackingDemo />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <IntegrationsSection />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="200px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <ContactSection />
          </Suspense>
        </LazySection>
        <LazySection rootMargin="100px 0px">
          <Suspense fallback={<SectionSkeleton />}>
            <Footer onChangeBusinessType={onChangeBusinessType} />
          </Suspense>
        </LazySection>
      </div>
    )
  }

  return null
}

export default LandingPage
